import React, { Component } from "react";
import { alert, t } from "./CustomComponent.js";
import { searchPlaces, placeDetail } from "./location_option.js";

/* ============================================================================
 * ช่องค้นหาสถานที่พร้อมรายการแนะนำ (Google Places API New)
 *
 * ใช้ร่วมกันระหว่าง
 *   - จัดการทรัพย์ › เพิ่ม/แก้ไขทรัพย์ (page/admin/asset_create.js) ขั้นที่ 1
 *   - จัดการทรัพย์ › โครงการ         (page/admin/projects.js)
 *
 * ทำ dropdown เองแทนการใช้ <gmp-place-autocomplete> ของ Google เพราะ web component
 * ตัวนั้นมี shadow DOM จัดสไตล์ให้เข้ากับ .prop-field ของโปรเจกต์ไม่ได้
 *
 * ตัวคอมโพเนนต์ไม่ถือค่าข้อความเอง (controlled) — หน้าที่เรียกเก็บไว้ในฟอร์มของตัวเอง
 * เพื่อให้ตอนเปิดฟอร์มแก้ไขเติมชื่อโครงการเดิมลงช่องได้
 *
 * props :
 *   value    {string}   ข้อความในช่อง
 *   onChange {function} (text) => void            ผู้ใช้พิมพ์ หรือเลือกจากรายการ
 *   onPick   {function} ({lat,lng,zone,name}) => void  เลือกสถานที่แล้ว (zone อาจเป็น null
 *                       ถ้าจับคู่จังหวัดกับ th_province.json ไม่ได้ เช่นสถานที่นอกไทย)
 * ========================================================================== */

const MIN_KEYWORD = 2; // สั้นกว่านี้ผลลัพธ์กว้างเกินจนไม่มีประโยชน์ และเปลืองโควตา
const DEBOUNCE_MS = 350; // Places คิดเงินต่อ session ไม่ใช่ต่อตัวอักษร แต่หน่วงไว้ลดคำขอที่ทิ้งเปล่า

export default class PlaceSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      list: [],
      open: false,
      searching: false,
      error: "", // คีย์ข้อความจาก MAP_ERROR — โชว์ในกล่อง dropdown ไม่ใช่ป๊อปอัป
      active: -1, // แถวที่เลือกอยู่ด้วยลูกศรขึ้น/ลง
    };
  }

  /* ค้างอยู่ระหว่าง debounce แล้วปิด modal → ต้องยกเลิก ไม่งั้น setState หลัง unmount */
  componentWillUnmount() {
    clearTimeout(this.timer);
    this.alive = false;
  }

  alive = true;
  timer = null;
  seq = 0; // ลำดับคำขอ — ผลที่กลับมาช้ากว่าคำขอล่าสุดต้องถูกทิ้ง ไม่งั้นรายการเก่าทับใหม่

  Type = (text) => {
    this.props.onChange(text);
    clearTimeout(this.timer);

    if (text.trim().length < MIN_KEYWORD) {
      this.seq += 1; // ตัดผลของคำขอที่ยังค้างอยู่ทิ้ง
      this.setState({ list: [], open: false, searching: false, error: "", active: -1 });
      return;
    }

    this.setState({ searching: true, open: true, error: "" });
    this.timer = setTimeout(() => this.Search(text), DEBOUNCE_MS);
  };

  Search = async (text) => {
    const seq = (this.seq += 1);
    const { list, error } = await searchPlaces(text);
    if (!this.alive || seq !== this.seq) return;

    this.setState({ list, error: error || "", searching: false, open: true, active: -1 });
  };

  /* เลือกจากรายการ → ดึงพิกัด/ที่อยู่ของสถานที่นั้นมาส่งให้หน้าที่เรียก */
  Pick = async (item) => {
    clearTimeout(this.timer);
    this.seq += 1;
    this.setState({ open: false, list: [], searching: true, error: "" });

    const result = await placeDetail(item.prediction);
    if (!this.alive) return;
    this.setState({ searching: false });

    if (result.error) return alert("", "warning", t("warning"), t(result.error));

    this.props.onChange(item.main || this.props.value);
    this.props.onPick(result);
  };

  Key = (e) => {
    const { list, open, active } = this.state;

    if (e.key === "Escape") return this.setState({ open: false });

    /* Enter ในฟอร์ม modal จะไปกดปุ่มแรกของฟอร์ม จึงต้องกันไว้เสมอ ไม่ใช่เฉพาะตอนมีรายการ */
    if (e.key === "Enter") {
      e.preventDefault();
      if (open && list[active]) this.Pick(list[active]);
      return;
    }

    if (!open || !list.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({ active: (active + 1) % list.length });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({ active: active <= 0 ? list.length - 1 : active - 1 });
    }
  };

  renderList = () => {
    const { list, searching, error, active } = this.state;

    if (searching) return <div className="pf-mapsearch__msg">{t("place_searching")}</div>;
    if (error) return <div className="pf-mapsearch__msg">{t(error)}</div>;
    if (!list.length) return null;

    return list.map((item, index) => (
      <button
        key={item.id || index}
        type="button"
        className={`pf-mapsearch__item ${index === active ? "is-active" : ""}`}
        onClick={() => this.Pick(item)}
      >
        <span className="icon pf-mapsearch__pin">{"\uf3c5"}</span>
        <span className="pf-mapsearch__text">
          <span className="pf-mapsearch__main">{item.main}</span>
          {item.secondary && <span className="pf-mapsearch__sub">{item.secondary}</span>}
        </span>
      </button>
    ));
  };

  render() {
    const { value } = this.props;
    const { open, searching, list, error } = this.state;
    const showList = open && (searching || error || list.length > 0);

    return (
      /* onMouseDown + preventDefault : กันไม่ให้ input เสีย focus ก่อน onClick ของรายการจะทำงาน
         (ถ้าใช้ onBlur ปิด dropdown เฉย ๆ รายการจะหายไปก่อนที่คลิกจะถึง) */
      <div className="pf-mapsearch" onMouseDown={(e) => e.target !== this.input && e.preventDefault()}>
        <input
          ref={(el) => (this.input = el)}
          type="text"
          className="prop-field"
          placeholder={t("place_search_placeholder")}
          value={value}
          onChange={(e) => this.Type(e.target.value)}
          onFocus={() => this.state.list.length && this.setState({ open: true })}
          onKeyDown={this.Key}
        />

        <button
          type="button"
          className="pf-field-btn"
          title={t("place_search")}
          disabled={searching}
          /* ไม่มีรายการค้าง (เช่นเพิ่งเปิดฟอร์มแก้ไขที่มีชื่อเดิมอยู่แล้ว) → กดปุ่มเพื่อค้นเลย */
          onClick={() => (list.length ? this.setState({ open: true }) : this.Type(value))}
        >
          <span className="icon">{searching ? "\uf1ce" : "\uf002"}</span>
        </button>

        {showList && <div className="pf-mapsearch__list">{this.renderList()}</div>}
      </div>
    );
  }
}
